'use client';

import { useEffect, useState } from 'react';

export type MediaDeviceOption = { deviceId: string; label: string };

type MediaDevices = {
	audioInputs: MediaDeviceOption[];
	videoInputs: MediaDeviceOption[];
};

const EMPTY_DEVICES: MediaDevices = { audioInputs: [], videoInputs: [] };

function toOptions(devices: MediaDeviceInfo[], kind: MediaDeviceKind, fallback: string) {
	return (
		devices
			// Chrome's "default" entry is kept: the open track can report it as its deviceId
			.filter((device) => device.kind === kind && device.deviceId)
			.map((device, index) => ({
				deviceId: device.deviceId,
				label: device.label || `${fallback} ${index + 1}`,
			}))
	);
}

/**
 * Mics and cameras on this device, kept fresh on plug/unplug.
 * Labels are only filled once camera/mic permission is granted, so enable it after the stream opens.
 */
export function useMediaDevices(enabled: boolean): MediaDevices {
	const [devices, setDevices] = useState<MediaDevices>(EMPTY_DEVICES);

	useEffect(() => {
		const mediaDevices = navigator.mediaDevices;
		if (!enabled || !mediaDevices?.enumerateDevices) return;

		let cancelled = false;
		const refresh = async () => {
			const list = await mediaDevices.enumerateDevices().catch(() => []);
			if (cancelled) return;
			setDevices({
				audioInputs: toOptions(list, 'audioinput', 'Microphone'),
				videoInputs: toOptions(list, 'videoinput', 'Camera'),
			});
		};

		refresh();
		mediaDevices.addEventListener('devicechange', refresh);
		return () => {
			cancelled = true;
			mediaDevices.removeEventListener('devicechange', refresh);
		};
	}, [enabled]);

	return devices;
}
